import React, { useEffect, useMemo, useState } from 'react';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { CreateUserRequestType } from 'api';
import { CommonModalType, ReturnComponentType } from 'common';
import { CommonModal } from 'components/Modals/CommonModal';
import {
  commonValidation,
  confirmPasswordValidation,
  createPasswordValidation,
  emailValidation,
  updatePasswordValidation,
} from 'config';
import { CommonCharacters, Roles } from 'enums';
import {
  useAppSelector,
  useSelectCommunities,
  useSelectProperties,
  useTypedDispatch,
} from 'hooks';
import {
  createUser,
  editUser,
  fetchUsers,
  selectCertainUser,
  selectUser,
  UserType,
} from 'state';
import { setCertainUser } from 'state/actions';
import { Button, InputForm, Select } from 'ui-kit';
import { ISelectOption } from 'ui-kit/Select/Select';
import {
  checkIsCustomer,
  checkIsGoodStatus,
  chooseAvatarFromRole,
  chooseTitleFromRole,
} from 'utils';
import './ModalAddUser.scss';

type ModalAddLocalAdminType = CommonModalType & {
  userId?: string;
  createdUserRole?: Roles;
  cancelEditUserModal: () => void;
  closeEditUserModal: () => void;
};

const customerOptions: ISelectOption[] = [
  { value: Roles.COMMUNITY_CUSTOMER, label: 'Community' },
  { value: Roles.PROPERTY_CUSTOMER, label: 'Property' },
];

export const ModalAddUser = ({
  showModal,
  userId,
  createdUserRole,
  cancelEditUserModal,
  closeEditUserModal,
}: ModalAddLocalAdminType): ReturnComponentType => {
  const dispatch = useTypedDispatch();
  const user = useAppSelector(selectCertainUser);
  const currentUser = useAppSelector(selectUser);
  const [isPasswordSectionVisible, setIsPasswordSectionVisible] = useState(false);

  const {
    propertiesData,
    selectOptions: selectPropertiesOptions,
    onChangeSelect: onChangePropertiesSelect,
    onFocusSelect: onFocusPropertiesSelect,
    selectedValue: selectedPropertiesValue,
    onMenuScrollToBottom: onMenuPropertiesScrollToBottom,
    onInputChangeSelect: onInputChangePropertiesSelect,
  } = useSelectProperties({ user });

  const {
    selectOptions: selectCommunitiesOptions,
    onChangeSelect: onChangeCommunitiesSelect,
    onFocusSelect: onFocusCommunitiesSelect,
    selectedValue: selectedCommunitiesValue,
    onMenuScrollToBottom: onMenuCommunitiesScrollToBottom,
    onInputChangeSelect: onInputChangeCommunitiesSelect,
  } = useSelectCommunities({ getOptions: false, user });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm<CreateUserRequestType>({
    defaultValues: {
      email: user?.email ?? '',
      password: userId ? CommonCharacters.HIDDEN_PASSWORD : '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      role: checkIsCustomer(user?.role)
        ? (customerOptions.find(item => item.value === user?.role)?.value as Roles)
        : undefined,
    },
  });

  useEffect(() => {
    // if the user is assigned to all the communities on the first page (or first n pages), we need to grab the next page to show
    if (selectCommunitiesOptions.length === 0) {
      onMenuCommunitiesScrollToBottom();
    }
  }, [selectCommunitiesOptions]);

  useEffect(() => {
    // if the user is assigned to all the properties on the first page (or first n pages), we need to grab the next page to show
    if (selectPropertiesOptions.length === 0) {
      onMenuPropertiesScrollToBottom();
    }
  }, [selectPropertiesOptions]);

  const cancelAddUserModal = () => {
    dispatch(setCertainUser({} as UserType));
    cancelEditUserModal();
    setIsPasswordSectionVisible(false);
  };

  const closeAddUserModal = () => {
    dispatch(setCertainUser({} as UserType));
    closeEditUserModal();
    setIsPasswordSectionVisible(false);
  };

  const onSubmit: SubmitHandler<CreateUserRequestType> = async ({
    email,
    password,
    firstName,
    lastName,
    role,
  }) => {
    const status = await dispatch(
      userId
        ? editUser({
            id: userId,
            firstName,
            lastName,
            email,
            password,
            role:
              checkIsCustomer(user?.role) && typeof role === 'object'
                ? // @ts-ignore
                  role.value
                : user?.role,
            companyId: currentUser.company?.id,
            communityIds:
              // @ts-ignore
              role?.value !== Roles.PROPERTY_CUSTOMER && role !== Roles.PROPERTY_CUSTOMER
                ? selectedCommunitiesValue?.map(item => item.value) ?? []
                : propertiesData?.map(property => property.community.id) ?? [],
            propertiesIds: selectedPropertiesValue?.map(item => item.value) ?? [],
          })
        : createUser({
            email,
            password,
            passwordConfirm: password,
            firstName,
            lastName,
            phone: '',
            localAdminId: '',
            role: checkIsCustomer(createdUserRole)
              ? // @ts-ignore
                role?.value
              : createdUserRole,
            companyId: currentUser.company?.id,
            communityIds:
              // @ts-ignore
              role?.value !== Roles.PROPERTY_CUSTOMER
                ? selectedCommunitiesValue?.map(item => item.value) ?? []
                : propertiesData?.map(property => property.community.id) ?? [],
            propertiesIds: selectedPropertiesValue?.map(item => item.value) ?? [],
          }),
    );

    if (checkIsGoodStatus(status)) {
      dispatch(fetchUsers(false));
      reset();
      cancelAddUserModal();
    }
  };

  const searchField = useMemo(() => {
    if (
      (createdUserRole === Roles.COMMUNITY_CUSTOMER &&
        !user?.role &&
        !watch('role')?.value) ||
      createdUserRole === Roles.SUPER_ADMIN ||
      user?.role === Roles.SUPER_ADMIN
    )
      return null;

    if (
      // @ts-ignore
      Roles.PROPERTY_CUSTOMER === watch('role')?.value ||
      (Roles.PROPERTY_CUSTOMER === user?.role &&
        // @ts-ignore
        Roles.COMMUNITY_CUSTOMER !== watch('role')?.value)
    ) {
      return (
        <>
          <span className="ModalAddUser_Form_Span">Properties</span>
          <Select
            className="ModalAddUser_Form_Search"
            onMenuScrollToBottom={onMenuPropertiesScrollToBottom}
            value={selectedPropertiesValue}
            options={selectPropertiesOptions}
            isSearchSelect
            isMulti
            closeMenuOnSelect={false}
            onFocus={onFocusPropertiesSelect}
            onInputChange={onInputChangePropertiesSelect}
            // @ts-ignore
            onChange={value => onChangePropertiesSelect(value)}
            maxMenuHeight={180}
          />
        </>
      );
    }
    if (Roles.INSPECTOR !== user?.role && Roles.INSPECTOR !== createdUserRole) {
      return (
        <>
          <span className="ModalAddUser_Form_Span">Communities</span>
          <div className="ModalAddUser_Form_SearchArea">
            <Select
              className="ModalAddUser_Form_Search"
              onMenuScrollToBottom={onMenuCommunitiesScrollToBottom}
              value={selectedCommunitiesValue}
              options={selectCommunitiesOptions}
              isSearchSelect
              isMulti
              closeMenuOnSelect={false}
              onFocus={onFocusCommunitiesSelect}
              onInputChange={onInputChangeCommunitiesSelect}
              // @ts-ignore
              onChange={value => onChangeCommunitiesSelect(value)}
              maxMenuHeight={180}
            />
          </div>
        </>
      );
    }
  }, [
    createdUserRole,
    onChangeCommunitiesSelect,
    onChangePropertiesSelect,
    onFocusCommunitiesSelect,
    onFocusPropertiesSelect,
    onMenuCommunitiesScrollToBottom,
    onMenuPropertiesScrollToBottom,
    selectCommunitiesOptions,
    selectPropertiesOptions,
    selectedCommunitiesValue,
    selectedPropertiesValue,
    user?.role,
    watch('role'),
  ]);

  return (
    <CommonModal closeModal={closeAddUserModal} showModal={showModal}>
      <div className="ModalAddUser">
        <header className="ModalAddUser_Header">
          <div className="ModalAddUser_Header_Title">
            {createdUserRole ? 'Add' : 'Edit'}{' '}
            {chooseTitleFromRole(user?.role ?? createdUserRole)}
          </div>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div onClick={closeAddUserModal} className="ModalAddUser_Header_Close">
            &#10006;
          </div>
        </header>

        <form className="ModalAddUser_Form" onSubmit={handleSubmit(onSubmit)}>
          <section>
            <img
              className="ModalAddUser_Form_Avatar"
              src={chooseAvatarFromRole(user?.role ?? createdUserRole)}
              alt="local-admin-logo"
            />
            <h4>{chooseTitleFromRole(user?.role ?? createdUserRole)}</h4>
          </section>

          <section>
            <Controller
              control={control}
              name="firstName"
              rules={commonValidation}
              render={({ field }) => (
                <InputForm
                  label="First name"
                  className="ModalAddUser_Form_Input"
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.firstName?.message}
                  autoFocus
                />
              )}
            />
            <Controller
              control={control}
              name="lastName"
              rules={commonValidation}
              render={({ field }) => (
                <InputForm
                  label="Last name"
                  className="ModalAddUser_Form_Input"
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.lastName?.message}
                />
              )}
            />
          </section>

          <section>
            <Controller
              control={control}
              name="email"
              rules={emailValidation}
              render={({ field }) => (
                <InputForm
                  label="Email"
                  className="ModalAddUser_Form_Input"
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.email?.message}
                />
              )}
            />
          </section>
          <section>
            {userId && !isPasswordSectionVisible && (
              <Button
                className="ModalAddUser_Form_Button-change-password"
                onClick={() => setIsPasswordSectionVisible(true)}
              >
                Change Password
              </Button>
            )}
            {(!userId || isPasswordSectionVisible) && (
              <>
                <Controller
                  control={control}
                  name="password"
                  rules={userId ? updatePasswordValidation : createPasswordValidation}
                  render={({ field }) => (
                    <InputForm
                      label="Password"
                      type="password"
                      className="ModalAddUser_Form_Input"
                      value={field.value}
                      onChange={e => field.onChange(e)}
                      error={errors.password?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="passwordConfirm"
                  rules={
                    !userId || watch('password') !== CommonCharacters.HIDDEN_PASSWORD
                      ? confirmPasswordValidation
                      : {}
                  }
                  render={({ field }) => (
                    <InputForm
                      label="Confirm password"
                      type="password"
                      className="ModalAddUser_Form_Input"
                      value={field.value}
                      onChange={e => field.onChange(e)}
                      error={errors.passwordConfirm?.message}
                    />
                  )}
                />
              </>
            )}
          </section>
          {(checkIsCustomer(user?.role) || checkIsCustomer(createdUserRole)) && (
            <section>
              <Controller
                control={control}
                name="role"
                rules={commonValidation}
                render={({ field }) => (
                  <Select
                    label="Type"
                    value={customerOptions.find(item => item.value === field.value)}
                    onChange={e => field.onChange(e)}
                    options={customerOptions}
                    className="ModalAddUser_Form_Select"
                    error={errors.role?.message}
                  />
                )}
              />
            </section>
          )}
          {searchField}
          <section className="ModalAddUser_Buttons">
            <Button type="submit" className="ModalAddUser_Form_Submit">
              Save
            </Button>
          </section>
        </form>
      </div>
    </CommonModal>
  );
};
